import { useState, useRef, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import SEO from '@/components/seo/SEO';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import { Bot, Send, Check, AlertTriangle, Sparkles, Clock, Shield } from 'lucide-react';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api/blockchain/v2/vendor-registration`;

const VENDOR_CATEGORIES = [
  { value: 'mattress', label: 'Mattress' },
  { value: 'linen', label: 'Linen' },
  { value: 'towel', label: 'Towel' },
  { value: 'pillow', label: 'Pillow' },
  { value: 'body_care', label: 'Body Care' },
  { value: 'ffe', label: 'Furniture / FF&E' },
  { value: 'minibar', label: 'Minibar' },
  { value: 'amenities', label: 'Amenities' },
  { value: 'electronics', label: 'Electronics' },
  { value: 'decor', label: 'Decor' },
  { value: 'lighting', label: 'Lighting' },
  { value: 'flooring', label: 'Flooring' },
  { value: 'kitchenware', label: 'Kitchenware' },
];

// Conversation script — each step asks one focused question
const STEPS = [
  { key: 'intro',                   kind: 'intro' },
  { key: 'company_name',            kind: 'text',       q: "Hi, I'm Vera. I'll walk you through joining the BHERMES vendor protocol in about 4 minutes. What's your company name?", placeholder: 'e.g. CloudNine Mattress Co.', required: true },
  { key: 'categories',              kind: 'categories', q: "Got it. Which product categories do you supply? Pick all that apply.", required: true },
  { key: 'description',             kind: 'textarea',   q: "In one or two sentences, what do you make?", placeholder: 'e.g. Hotel-grade memory foam mattresses with a 10-year warranty…' },
  { key: 'contact_email',           kind: 'text',       q: "What's the best contact email for business inquiries?", placeholder: 'sales@example.com', required: true, validate: (v) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(v) || 'That doesn\'t look like a valid email.' },
  { key: 'phone_number',            kind: 'text',       q: "Phone number? (optional — helps us reach you for approval)", placeholder: '+1 555 123 4567' },
  { key: 'website',                 kind: 'text',       q: "Your company website? (optional but adds 20 points to your trust score)", placeholder: 'https://example.com' },
  { key: 'business_registration_id',kind: 'text',       q: "Business registration ID or VAT number? (optional but adds 20 points to your trust score)", placeholder: 'e.g. 123456789' },
  { key: 'physical_address',        kind: 'text',       q: "Physical business address? (optional)", placeholder: '500 Main St' },
  { key: 'city_country',            kind: 'city_country', q: "Which city and country are you based in?" },
  { key: 'review',                  kind: 'review' },
];

export default function RegisterVendorPage() {
  const [stepIdx, setStepIdx] = useState(0);
  const [answers, setAnswers] = useState({ categories: [] });
  const [inputVal, setInputVal] = useState('');
  const [cityVal, setCityVal] = useState('');
  const [countryVal, setCountryVal] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState(null);
  const [transcript, setTranscript] = useState([
    { from: 'vera', text: "Hi, I'm Vera — the BHERMES vendor onboarding agent." },
    { from: 'vera', text: "Press Start and I'll guide you through it." },
  ]);
  const scrollRef = useRef(null);
  const navigate = useNavigate();

  const step = STEPS[stepIdx];

  useEffect(() => {
    if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
  }, [transcript]);

  const push = (from, text) => setTranscript((t) => [...t, { from, text }]);

  const start = () => {
    setStepIdx(1);
    push('vera', STEPS[1].q);
  };

  const advance = (fromKey, value, displayText) => {
    // Validate
    if (step.required && (!value || (Array.isArray(value) && value.length === 0))) {
      toast.error('This one is required.');
      return;
    }
    if (step.validate && value) {
      const v = step.validate(value);
      if (typeof v === 'string') { toast.error(v); return; }
    }

    // Special handling for the city+country merged step
    if (fromKey === '__city_country' && value && typeof value === 'object') {
      setAnswers((a) => ({ ...a, city: value.city, country: value.country }));
    } else {
      setAnswers((a) => ({ ...a, [fromKey]: value }));
    }
    push('user', displayText || (Array.isArray(value) ? value.join(', ') : (value || '(skipped)')));

    const next = stepIdx + 1;
    if (next < STEPS.length) {
      setStepIdx(next);
      const nextStep = STEPS[next];
      if (nextStep.q) push('vera', nextStep.q);
      setInputVal('');
      setCityVal('');
      setCountryVal('');
    }
  };

  const submit = async () => {
    setSubmitting(true);
    try {
      const payload = {
        company_name: answers.company_name || '',
        contact_email: answers.contact_email || '',
        phone_number: answers.phone_number || '',
        website: answers.website || '',
        business_registration_id: answers.business_registration_id || '',
        physical_address: answers.physical_address || '',
        city: answers.city || '',
        country: answers.country || '',
        description: answers.description || '',
        categories: answers.categories || [],
      };
      const r = await axios.post(`${API}/submit`, payload);
      setResult(r.data);
      push('vera', r.data.status === 'rejected'
        ? 'Unfortunately, the verification flagged an issue. Details on the right.'
        : `Excellent — you scored ${r.data.verification?.total_score || 0}/100 on anti-Sybil verification. Details on the right.`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Submission failed. Try again.');
    } finally {
      setSubmitting(false);
    }
  };

  // ─── RESULT SCREEN ─────────────────────────────────
  if (result) return <ResultView result={result} onNewVendor={() => window.location.reload()} onHome={() => navigate('/')} />;

  // ─── MAIN TWO-PANEL UI ─────────────────────────────
  return (
    <div className="min-h-screen bg-[#0a0e1a] text-zinc-100" data-testid="register-vendor-page">
      <SEO
        title="Join the BHERMES Vendor Protocol — Register in 4 Minutes"
        description="Conversational vendor onboarding with Vera AI. 6-layer anti-Sybil verification, 4-phase staged token mint (25K $HERMES Era 1), quality-slashable staking. Supply hotel products through the agentic hospitality OS."
        canonical="https://getbitsy.ai/register-vendor"
      />
      <header className="border-b border-zinc-800/60 px-6 py-4 flex justify-between items-center">
        <Link to="/" className="text-sm tracking-widest text-zinc-400 hover:text-zinc-200">← getbitsy.ai</Link>
        <Badge variant="outline" className="border-zinc-700 text-zinc-400 text-[10px] tracking-widest">
          VENDOR ONBOARDING
        </Badge>
      </header>

      <main className="max-w-6xl mx-auto grid md:grid-cols-[1fr_360px] gap-6 px-6 py-10">
        {/* LEFT — Chat panel */}
        <Card className="bg-zinc-900 border-zinc-800 flex flex-col h-[70vh]">
          <CardContent className="p-5 flex-1 overflow-y-auto space-y-3" ref={scrollRef} data-testid="chat-scroll">
            {transcript.map((m, i) => <Bubble key={i} from={m.from} text={m.text} />)}
          </CardContent>
          <div className="border-t border-zinc-800 p-4 bg-zinc-900/80">
            <StepInput
              step={step}
              answers={answers}
              inputVal={inputVal}
              setInputVal={setInputVal}
              cityVal={cityVal}
              setCityVal={setCityVal}
              countryVal={countryVal}
              setCountryVal={setCountryVal}
              onStart={start}
              onAdvance={advance}
              onSubmit={submit}
              onToggleCategory={(c) => setAnswers((a) => ({ ...a, categories: a.categories.includes(c) ? a.categories.filter((x) => x !== c) : [...a.categories, c] }))}
              submitting={submitting}
            />
          </div>
        </Card>

        {/* RIGHT — Live preview */}
        <LivePreview answers={answers} />
      </main>
    </div>
  );
}

function Bubble({ from, text }) {
  const isVera = from === 'vera';
  return (
    <div className={`flex ${isVera ? 'justify-start' : 'justify-end'}`}>
      <div className={`flex gap-2 items-start max-w-[85%] ${isVera ? '' : 'flex-row-reverse'}`}>
        <div className={`w-7 h-7 rounded-full flex items-center justify-center text-xs ${isVera ? 'bg-amber-500/20 text-amber-400' : 'bg-zinc-700 text-zinc-200'}`}>
          {isVera ? <Bot className="w-4 h-4" /> : 'U'}
        </div>
        <div className={`rounded-xl px-3.5 py-2.5 text-sm ${isVera ? 'bg-zinc-800 text-zinc-100' : 'bg-amber-500 text-black'}`}>{text}</div>
      </div>
    </div>
  );
}

function StepInput({ step, answers, inputVal, setInputVal, cityVal, setCityVal, countryVal, setCountryVal,
                    onStart, onAdvance, onSubmit, onToggleCategory, submitting }) {
  if (step.kind === 'intro') {
    return (
      <Button size="lg" onClick={onStart} className="w-full bg-amber-500 hover:bg-amber-600 text-black" data-testid="start-vendor-registration">
        <Sparkles className="w-4 h-4 mr-2" /> Start registration
      </Button>
    );
  }

  if (step.kind === 'text') {
    return (
      <form onSubmit={(e) => { e.preventDefault(); onAdvance(step.key, inputVal); }} className="flex gap-2">
        <Input autoFocus value={inputVal} onChange={(e) => setInputVal(e.target.value)}
          placeholder={step.placeholder || ''} className="bg-zinc-800 border-zinc-700"
          data-testid={`input-${step.key}`} />
        <Button type="submit" className="bg-amber-500 hover:bg-amber-600 text-black" data-testid="send-step-btn">
          <Send className="w-4 h-4" />
        </Button>
      </form>
    );
  }

  if (step.kind === 'textarea') {
    return (
      <form onSubmit={(e) => { e.preventDefault(); onAdvance(step.key, inputVal); }} className="flex gap-2">
        <Textarea autoFocus value={inputVal} onChange={(e) => setInputVal(e.target.value)}
          placeholder={step.placeholder || ''} className="bg-zinc-800 border-zinc-700 min-h-[60px]"
          rows={2} data-testid={`input-${step.key}`} />
        <Button type="submit" className="bg-amber-500 hover:bg-amber-600 text-black self-end" data-testid="send-step-btn">
          <Send className="w-4 h-4" />
        </Button>
      </form>
    );
  }

  if (step.kind === 'categories') {
    return (
      <div className="space-y-3">
        <div className="flex flex-wrap gap-2">
          {VENDOR_CATEGORIES.map((c) => (
            <button key={c.value} type="button" onClick={() => onToggleCategory(c.value)}
              data-testid={`category-${c.value}`}
              className={`px-3 py-1.5 rounded-full text-xs border transition
                ${answers.categories.includes(c.value)
                  ? 'bg-amber-500 text-black border-amber-500'
                  : 'bg-zinc-800 text-zinc-300 border-zinc-700 hover:border-zinc-600'}`}>
              {c.label}
            </button>
          ))}
        </div>
        <Button onClick={() => onAdvance('categories', answers.categories)} disabled={answers.categories.length === 0}
          className="w-full bg-amber-500 hover:bg-amber-600 text-black" data-testid="confirm-categories-btn">
          Next {answers.categories.length > 0 && `(${answers.categories.length} selected)`}
        </Button>
      </div>
    );
  }

  if (step.kind === 'city_country') {
    return (
      <form onSubmit={(e) => {
        e.preventDefault();
        // Advance once with merged city+country into answers; advance() also pushes user bubble
        onAdvance('__city_country', { city: cityVal, country: countryVal }, `${cityVal}, ${countryVal}`);
      }} className="grid grid-cols-2 gap-2">
        <Input autoFocus value={cityVal} onChange={(e) => setCityVal(e.target.value)}
          placeholder="City (e.g. Lisbon)" className="bg-zinc-800 border-zinc-700" data-testid="input-city" />
        <Input value={countryVal} onChange={(e) => setCountryVal(e.target.value)}
          placeholder="Country (e.g. Portugal)" className="bg-zinc-800 border-zinc-700" data-testid="input-country" />
        <Button type="submit" disabled={!cityVal || !countryVal} className="col-span-2 bg-amber-500 hover:bg-amber-600 text-black" data-testid="confirm-city-country-btn">
          Next
        </Button>
      </form>
    );
  }

  if (step.kind === 'review') {
    return (
      <Button onClick={onSubmit} disabled={submitting} size="lg" className="w-full bg-emerald-500 hover:bg-emerald-600 text-black" data-testid="submit-registration-btn">
        {submitting ? 'Submitting to protocol…' : 'Submit to BHERMES Protocol'}
      </Button>
    );
  }

  return null;
}

function LivePreview({ answers }) {
  const filled = [
    { label: 'Company',      v: answers.company_name },
    { label: 'Categories',   v: (answers.categories || []).join(', ') },
    { label: 'Description',  v: answers.description },
    { label: 'Email',        v: answers.contact_email },
    { label: 'Phone',        v: answers.phone_number },
    { label: 'Website',      v: answers.website },
    { label: 'Business ID',  v: answers.business_registration_id },
    { label: 'Address',      v: answers.physical_address },
    { label: 'City, Country',v: [answers.city, answers.country].filter(Boolean).join(', ') },
  ];

  // Estimate anti-Sybil score preview
  let score = 0;
  if (answers.company_name) score += 15;
  if (answers.contact_email) score += 10;
  if (answers.website) score += 20;
  if (answers.business_registration_id) score += 20;
  if ((answers.categories || []).length > 0) score += 20;
  score += 15; // burst passes by default
  score = Math.min(score, 100);

  return (
    <Card className="bg-zinc-900 border-zinc-800 sticky top-6 h-fit">
      <CardContent className="p-5 space-y-5" data-testid="vendor-live-preview">
        <div>
          <p className="text-xs tracking-widest text-zinc-500">LIVE VENDOR PROFILE</p>
          <p className="font-medium text-zinc-100 mt-1">{answers.company_name || 'Your company'}</p>
        </div>

        <div className="space-y-1.5">
          {filled.filter(f => f.v).map((f) => (
            <div key={f.label} className="flex justify-between text-xs gap-2">
              <span className="text-zinc-500">{f.label}</span>
              <span className="text-zinc-200 text-right truncate max-w-[60%]">{f.v}</span>
            </div>
          ))}
        </div>

        {/* Trust score preview */}
        <div className="border-t border-zinc-800 pt-4 space-y-2">
          <div className="flex justify-between items-center">
            <span className="text-xs tracking-widest text-zinc-500">TRUST SCORE PREVIEW</span>
            <span className={`text-lg font-semibold ${score >= 70 ? 'text-emerald-400' : score >= 50 ? 'text-amber-400' : 'text-rose-400'}`}>
              {score}/100
            </span>
          </div>
          <div className="h-1.5 bg-zinc-800 rounded-full overflow-hidden">
            <div className={`h-full transition-all ${score >= 70 ? 'bg-emerald-400' : score >= 50 ? 'bg-amber-400' : 'bg-rose-400'}`}
                 style={{ width: `${score}%` }} />
          </div>
          <p className="text-[11px] text-zinc-500 leading-relaxed">
            {score >= 70 ? 'Auto-approval eligible.' : 'Submit will route to manual review. Add website + business ID to auto-approve.'}
          </p>
        </div>

        {/* Mint preview */}
        <div className="border-t border-zinc-800 pt-4 space-y-2">
          <p className="text-xs tracking-widest text-zinc-500">TOKEN MINT (ERA 1 BASE)</p>
          <p className="text-2xl font-semibold text-amber-400">25,000 $HERMES</p>
          <p className="text-[11px] text-zinc-500 leading-relaxed">
            Released in 4 phases over 90 days. 29% operations · 50% quality-slashable stake · 20% guest rewards.
          </p>
        </div>
      </CardContent>
    </Card>
  );
}

function ResultView({ result, onNewVendor, onHome }) {
  const rejected = result.status === 'rejected';
  const verification = result.verification || {};
  const mint = result.mint_preview || {};

  return (
    <div className="min-h-screen bg-[#0a0e1a] text-zinc-100 flex flex-col" data-testid="vendor-result-page">
      <header className="border-b border-zinc-800/60 px-6 py-4 flex justify-between items-center">
        <Link to="/" className="text-sm tracking-widest text-zinc-400 hover:text-zinc-200">← getbitsy.ai</Link>
        <Badge variant="outline" className="border-zinc-700 text-zinc-400 text-[10px] tracking-widest">REGISTRATION COMPLETE</Badge>
      </header>

      <main className="max-w-4xl mx-auto w-full px-6 py-16 space-y-10">
        {rejected ? (
          <Card className="bg-rose-600 border-0">
            <CardContent className="p-10 text-center text-white space-y-4">
              <AlertTriangle className="w-16 h-16 mx-auto" />
              <h1 className="text-3xl font-bold">Registration rejected</h1>
              <p className="text-rose-50">{result.verification?.checks?.find((c) => c.status === 'failed')?.details || 'Failed anti-Sybil checks.'}</p>
            </CardContent>
          </Card>
        ) : (
          <Card className="bg-emerald-600 border-0" data-testid="vendor-success-card">
            <CardContent className="p-10 text-center text-white space-y-3">
              <Check className="w-16 h-16 mx-auto" />
              <h1 className="text-3xl font-bold">You're in the queue</h1>
              <p className="text-emerald-50">Registration ID <code className="bg-black/25 px-2 py-0.5 rounded">{result.registration_id}</code></p>
            </CardContent>
          </Card>
        )}

        {/* Score breakdown */}
        <section className="space-y-3">
          <h2 className="text-xl font-semibold flex items-center gap-2">
            <Shield className="w-5 h-5 text-amber-400" /> Anti-Sybil verification: {verification.total_score || 0}/100
          </h2>
          <div className="space-y-2">
            {(verification.checks || []).map((c, i) => (
              <div key={i} className="flex justify-between items-start gap-4 p-3 rounded-lg bg-zinc-900 border border-zinc-800" data-testid={`check-${c.check}`}>
                <div className="flex-1">
                  <p className="text-sm font-medium text-zinc-100 capitalize">{c.check.replace(/_/g, ' ')}</p>
                  <p className="text-xs text-zinc-500 mt-0.5">{c.details}</p>
                </div>
                <Badge className={
                  c.status === 'passed' ? 'bg-emerald-500/15 text-emerald-400 border-emerald-500/30' :
                  c.status === 'failed' ? 'bg-rose-500/15 text-rose-400 border-rose-500/30' :
                  'bg-amber-500/15 text-amber-400 border-amber-500/30'
                }>
                  {c.status} · +{c.score}
                </Badge>
              </div>
            ))}
          </div>
        </section>

        {!rejected && (
          <section className="space-y-3">
            <h2 className="text-xl font-semibold flex items-center gap-2">
              <Clock className="w-5 h-5 text-amber-400" /> Staged mint schedule (90 days)
            </h2>
            <p className="text-zinc-400 text-sm">
              Your allocation of <b>{(mint.mint_amount || 25000).toLocaleString()} $HERMES</b> (Era {mint.era || 1}) releases in 4 phases.
              Pending human approval on the registration.
            </p>
            <div className="grid md:grid-cols-4 gap-3">
              {[
                { phase: 1, pct: 10, day: 0,  label: 'Initial',     cond: 'Approval' },
                { phase: 2, pct: 20, day: 30, label: 'Products',    cond: 'Listed + mapped' },
                { phase: 3, pct: 30, day: 60, label: 'Engagement',  cond: 'QR scans' },
                { phase: 4, pct: 40, day: 90, label: 'Full release',cond: 'Quality > 300' },
              ].map((p) => (
                <Card key={p.phase} className="bg-zinc-900 border-zinc-800">
                  <CardContent className="p-4">
                    <p className="text-xs tracking-widest text-zinc-500">PHASE {p.phase} · DAY {p.day}</p>
                    <p className="text-2xl font-semibold text-amber-400 mt-1">{p.pct}%</p>
                    <p className="text-sm text-zinc-100 mt-0.5">{p.label}</p>
                    <p className="text-[11px] text-zinc-500 mt-1">{p.cond}</p>
                  </CardContent>
                </Card>
              ))}
            </div>
          </section>
        )}

        <div className="flex gap-3 pt-4">
          <Button variant="outline" className="flex-1 border-zinc-700 text-zinc-200" onClick={onHome} data-testid="home-btn">Back to home</Button>
          {!rejected && (
            <Button className="flex-1 bg-amber-500 hover:bg-amber-600 text-black" onClick={onNewVendor} data-testid="another-vendor-btn">
              Register another
            </Button>
          )}
        </div>
      </main>
    </div>
  );
}
